import { useMemo, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ArrowSquareOutIcon, CaretDownIcon } from '@phosphor-icons/react';
import RightPanelShell from 'src/components/layouts/RightPanelShell';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';

const INSTALL_APP_IMAGES = {
  edge: 'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/install-app1.png',
  chrome: 'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/install-app2.png'
};

export default function QuickTips() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const [open, setOpen] = useState<Record<string, boolean>>({
    zoom: false,
    demo: false,
    edge: false,
    chrome: false
  });
  const toggle = (key: string) => setOpen((s) => ({ ...s, [key]: !s[key] }));
  const bookingUrl = useMemo(() => {
    const envUrl = import.meta?.env?.VITE_DEMO_BOOKING_URL as string | undefined;
    return envUrl && envUrl.length > 0 ? envUrl : 'https://calendly.com/carl-complimind/innex-onboarding-sessions';
  }, []);
  const feedbackUrl = useMemo(() => {
    const envUrl = import.meta?.env?.VITE_FEEDBACK_BOOKING_URL as string | undefined;
    return envUrl && envUrl.length > 0 ? envUrl : 'https://calendly.com/carl-complimind/feedback-chats';
  }, []);

  const browserType = useMemo<'edge' | 'chrome' | 'other'>(() => {
    const ua = navigator.userAgent;
    if (ua.includes('Edg/')) return 'edge';
    if (ua.includes('Chrome')) return 'chrome';
    return 'other';
  }, []);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Quick Tips</h2>
    </div>
  );

  const footer = (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-welcome-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-content-default">Quick Tips</h2>
          <p className="text-sm text-content-subtle">
            We have put together a few short guides to improve your experience with Innex
          </p>
        </div>

        {/* Accordions */}
        <div className="flex flex-col gap-2 w-full">
          <div className="border border-border-default rounded-md">
            <button
              className="w-full text-left px-3 py-2 text-sm font-semibold flex items-center justify-between"
              onClick={() => toggle('zoom')}
            >
              <span>Adjust your zoom level</span>
              <CaretDownIcon
                size={18}
                weight="light"
                className={`transition-transform ${open.zoom ? 'rotate-180' : ''}`}
              />
            </button>
            {open.zoom && (
              <div className="px-3 pb-3 text-sm text-content-subtle">
                Use your browser zoom to adjust the interface size: ⌘ + +/- (Mac) or Ctrl + +/- (Windows).
              </div>
            )}
          </div>

          <div className="border border-border-default rounded-md">
            <button
              className="w-full text-left px-3 py-2 text-sm font-semibold flex items-center justify-between"
              onClick={() => toggle('demo')}
            >
              <span>Book a live demo</span>
              <CaretDownIcon
                size={18}
                weight="light"
                className={`transition-transform ${open.demo ? 'rotate-180' : ''}`}
              />
            </button>
            {open.demo && (
              <div className="px-3 pb-3 text-sm text-content-subtle">
                <div>Want a guided walkthrough? Book a quick session with our team.</div>
                <div className="mt-2">
                  <a href={bookingUrl} target="_blank" rel="noreferrer">
                    <Button icon={ArrowSquareOutIcon} variant="primary" size="sm">
                      Book demo
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="border border-border-default rounded-md">
            <button
              className="w-full text-left px-3 py-2 text-sm font-semibold flex items-center justify-between"
              onClick={() => toggle('feedback')}
            >
              <span>Book a feedback session</span>
              <CaretDownIcon
                size={18}
                weight="light"
                className={`transition-transform ${open.feedback ? 'rotate-180' : ''}`}
              />
            </button>
            {open.feedback && (
              <div className="px-3 pb-3 text-sm text-content-subtle">
                <div>Want to share your feedback? Book a quick session with our team.</div>
                <div className="mt-2">
                  <a href={feedbackUrl} target="_blank" rel="noreferrer">
                    <Button icon={ArrowSquareOutIcon} variant="primary" size="sm">
                      Book feedback session
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </div>

          {(browserType === 'edge' || browserType === 'other') && (
            <div className="border border-border-default rounded-md">
              <button
                className="w-full text-left px-3 py-2 text-sm font-semibold flex items-center justify-between"
                onClick={() => toggle('edge')}
              >
                <span>Install CompliMind as an App - Edge</span>
                <CaretDownIcon
                  size={18}
                  weight="light"
                  className={`transition-transform ${open.edge ? 'rotate-180' : ''}`}
                />
              </button>
              {open.edge && (
                <div className="px-3 pb-3 text-sm text-content-subtle">
                  <ol className="list-decimal list-inside mb-2">
                    <li>Open the browser menu (⋯) in the top right.</li>
                    <li>Select "Apps" → "Install this site as an app".</li>
                    <li>Confirm installation. CompliMind appears in your Start menu and taskbar.</li>
                  </ol>
                  <img
                    src={INSTALL_APP_IMAGES.edge}
                    alt="Install app in Edge"
                    className="rounded border border-border-default"
                  />
                </div>
              )}
            </div>
          )}

          {(browserType === 'chrome' || browserType === 'other') && (
            <div className="border border-border-default rounded-md">
              <button
                className="w-full text-left px-3 py-2 text-sm font-semibold flex items-center justify-between"
                onClick={() => toggle('chrome')}
              >
                <span>Install CompliMind as an App - Chrome</span>
                <CaretDownIcon
                  size={18}
                  weight="light"
                  className={`transition-transform ${open.chrome ? 'rotate-180' : ''}`}
                />
              </button>
              {open.chrome && (
                <div className="px-3 pb-3 text-sm text-content-subtle">
                  <ol className="list-decimal list-inside mb-2">
                    <li>Click the install icon in the address bar.</li>
                    <li>Or open the menu (︙) → "Save and share" → "Install page as an app".</li>
                    <li>Confirm installation. Pin to your dock for quick access.</li>
                  </ol>
                  <img
                    src={INSTALL_APP_IMAGES.chrome}
                    alt="Install app in Chrome"
                    className="rounded border border-border-default"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </RightPanelShell>
  );
}
